/**
 * CurriculoBanner - Banner de lembrete para completar currículo
 * Exibido em todas as páginas do app mobile quando currículo está incompleto
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { candidatesAPI } from '../lib/api';
import { X, FileText, ArrowRight, AlertCircle } from 'lucide-react';

export default function CurriculoBanner() {
  const navigate = useNavigate();
  const [showBanner, setShowBanner] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkCurriculoStatus();
  }, []);

  const checkCurriculoStatus = async () => {
    try {
      const response = await candidatesAPI.getMe();
      const candidate = response.data;
      
      // Verificar se currículo está incompleto
      const isIncomplete = !candidate.cidade || !candidate.estado || candidate.esta_trabalhando === null;
      
      setShowBanner(isIncomplete);
    } catch (error) {
      // Se erro ao carregar, não mostrar banner
      console.error('Error checking curriculo status:', error);
      setShowBanner(false);
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    setShowBanner(false);
    // Nota: O banner reaparecerá na próxima vez que o usuário acessar o app
    // até que o currículo seja completado
  };

  const handleComplete = () => {
    navigate('/m/completar-curriculo');
  };

  if (loading || dismissed || !showBanner) {
    return null;
  }

  return (
    <div 
      className="fixed bottom-20 left-4 right-4 z-40 animate-in slide-in-from-bottom-4 duration-300"
      data-testid="curriculo-banner"
    >
      <div className="bg-gradient-to-r from-amber-600 to-orange-600 rounded-xl shadow-lg shadow-amber-500/30 p-4">
        <div className="flex items-start gap-3">
          {/* Icon */}
          <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
            <AlertCircle className="w-5 h-5 text-white" />
          </div>
          
          {/* Content */}
          <div className="flex-1 min-w-0">
            <h4 className="text-white font-semibold text-sm">Complete seu currículo</h4>
            <p className="text-amber-100 text-xs mt-0.5">
              Adicione sua localização e situação de emprego para receber vagas personalizadas.
            </p>
            
            <button
              onClick={handleComplete}
              className="mt-2 inline-flex items-center gap-1 text-white bg-white/20 hover:bg-white/30 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
            >
              <FileText className="w-3 h-3" />
              Completar agora
              <ArrowRight className="w-3 h-3" />
            </button>
          </div>
          
          {/* Close Button */}
          <button
            onClick={handleDismiss}
            className="text-white/70 hover:text-white p-1"
            aria-label="Fechar"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
